/**
 * LSEG / Refinitiv custom functions for Excel.
 *
 * Each function assembles the same Excel-style formula string the
 * lseg_interface formula bar accepts and POSTs it to /api/evaluate. The LSEG
 * functions (TR, RHistory, RtGet and their RDP.* names) lay their result out
 * as LSEG does (spec §5.3): the formula cell holds the top-left value or
 * `Updated at HH:MM:SS`, and the static writer writes the rest of the table
 * as plain values. The EAV-only functions spill their grid. All parsing, hub
 * translation, auth and audit logging stay server-side.
 *
 * Sign in once from the LSEG task pane; the JWT is shared with this runtime
 * via OfficeRuntime.storage.
 */

import { evaluate, ApiError } from "../lib/api";
import { openRtStream, splitList, throttle } from "../lib/stream";
import {
  buildCall,
  Built,
  CellError,
  historyGrid,
  LiveFeed,
  n,
  q,
  rhistoryTable,
  rtgetGrid,
  rtgetLive,
  toMatrix,
  toTable,
  trLive,
} from "../lib/grids";
import { DEST_OTHER_SHEET, destOnOtherSheet, planOutput, Table } from "../lib/layout";
import { errorToCellText, LSEG_TEXT, LsegFamily, searchFailureText, searchRefusal } from "../lib/lsegText";
import { flattenInstruments, substituteArgs } from "../lib/refinitiv";
import { planAggregate } from "../search/aggregate";
import { planRdpSearch, RDP_SEARCH_TEXT, rowCodes } from "../search/rdpSearch";
import { ADFIN_FUNCTIONS, ADFIN_FX_FUNCTIONS, AdFinFn, buildAdFinRequest } from "../adfin/processors";
import { layAdFinResponse } from "../adfin/responses";
import { AnalyticsBody, AnalyticsRequest, buildAnalyticsRequest } from "../analytics/request";
import { fieldListNames, fieldListRequest, hasInvalidField, ipaFieldList, respell } from "../analytics/ipaFields";
import { analyticsErrorText, layAnalyticsResponse } from "../analytics/response";
import { evaluateLimited } from "../lib/batcher";
import { createWriter, excelFormulaPort, FormulaPort, FormulaScope, Reentry, SheetRect } from "../lib/excelPort";
import { RELEASE_GRACE_MS, StaticWriter } from "../lib/writer";

/** Map a backend/auth failure to the Excel error the cell displays (RSEARCH, METADATA, …). */
function toCellError(e: any): CustomFunctions.Error {
  if (e instanceof CustomFunctions.Error) return e;
  const err = e as ApiError;
  if (err.status === 401 || err.status === 403) {
    return new CustomFunctions.Error(
      CustomFunctions.ErrorCode.notAvailable,
      "Not signed in — open the LSEG task pane and sign in."
    );
  }
  return new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, (err && err.message) || "LSEG request failed.");
}

async function run(formula: string): Promise<any[][]> {
  try {
    const resp = await evaluate(formula);
    return toMatrix(resp);
  } catch (e) {
    throw toCellError(e);
  }
}

/**
 * Run a grid builder (lib/grids.ts). Failures arrive as LSEG text cells; only
 * a CellError (a case with no LSEG text yet) becomes an Excel error.
 */
async function grid(build: () => Promise<any[][]>): Promise<any[][]> {
  try {
    return await build();
  } catch (e) {
    if (e instanceof CellError) {
      const code =
        e.code === "notAvailable" ? CustomFunctions.ErrorCode.notAvailable : CustomFunctions.ErrorCode.invalidValue;
      throw new CustomFunctions.Error(code, e.message);
    }
    throw toCellError(e);
  }
}

// --- LSEG layout (spec §5.3) ---------------------------------------------------

/** The static writer, created on first use (tests inject a fake with setWriter). */
let writer: StaticWriter | null = null;

function getWriter(): StaticWriter {
  if (!writer) writer = createWriter();
  return writer;
}

/** Replace the static writer (tests); null → the Excel-backed one on next use. */
export function setWriter(w: StaticWriter | null): void {
  writer = w;
}

/** The formula-cell port (re-entry, change events); tests inject a fake with setFormulaPort. */
let formulaPort: FormulaPort | null = null;

function getFormulaPort(): FormulaPort {
  if (!formulaPort) formulaPort = excelFormulaPort;
  return formulaPort;
}

/** Replace the formula port (tests); null → the Excel-backed one on next use. */
export function setFormulaPort(p: FormulaPort | null): void {
  formulaPort = p;
}

type Invocation = CustomFunctions.StreamingInvocation<any>;

/** A cell of an Excel address: the sheet name unquoted ("" when absent), 0-based row/col. */
interface CellAt {
  sheet: string;
  row: number;
  col: number;
}

const ADDRESS_RE = /^(?:(?:'((?:[^']|'')+)'|([^'!][^!]*))!)?\$?([A-Za-z]{1,3})\$?(\d+)(?::\S+)?$/;

/**
 * Parse "Sheet1!B2", "'My Sheet'!$B$2" or "B2"; a range ("E5:F6") gives its
 * top-left cell. Null for anything else (an omitted or non-reference argument).
 */
export function parseCellAddress(address: string | undefined | null): CellAt | null {
  const m = ADDRESS_RE.exec(String(address ?? "").trim());
  if (!m) return null;
  let col = 0;
  for (const ch of m[3].toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
  const sheet = m[1] !== undefined ? m[1].replace(/''/g, "'") : m[2] ?? "";
  return { sheet, row: Number(m[4]) - 1, col: col - 1 };
}

function columnName(col: number): string {
  let s = "";
  for (let c = col + 1; c > 0; c = Math.floor((c - 1) / 26)) s = String.fromCharCode(65 + ((c - 1) % 26)) + s;
  return s;
}

/** The text a failure shows, as LSEG's plain string (spec §5.2): never an Excel error. */
function failureText(e: unknown, family: LsegFamily): string {
  return e instanceof CellError ? e.message : errorToCellText(e, family);
}

/** A build whose failure becomes its LSEG text cell. */
function settledBuild(build: () => Promise<Built>, family: LsegFamily): Promise<Built> {
  return build().catch((e): Built => ({ table: toTable([[failureText(e, family)]]), live: null }));
}

/** A short, stable id of a call's signature (32-bit FNV-1a, base 36). */
function callId(signature: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < signature.length; i++) {
    h ^= signature.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * The key of one call in one cell: `Sheet!A1#<callId>`. A cell can hold
 * several LSEG calls (=RHistory(…)/RHistory(…)-1, IF(TR(…)=0,…,TR(…)/100)):
 * each has its own key, so its own live session, footprint and writer state.
 */
function callKey(at: CellAt, signature: string): string {
  return `${at.sheet}!${columnName(at.col)}${at.row + 1}#${callId(signature)}`;
}

const cellId = (at: CellAt): string => `${at.sheet.toLowerCase()}!${at.row},${at.col}`;

/** The Dest cell of an invocation: the sheet it names, else the formula's (another sheet is refused, spec §5.3 rule 3). */
function destOf(at: CellAt, destIndex: number | null, parameterAddresses: string[] | undefined): CellAt | null {
  const d = destIndex === null ? null : parseCellAddress(parameterAddresses?.[destIndex]);
  return d ? { sheet: d.sheet || at.sheet, row: d.row, col: d.col } : null;
}

/** The table of a refused call (a Dest on another sheet): planOutput shows the refusal text. */
const REFUSED: Table = { cells: [], cornerEmpty: false };

/** The formula-cell value of a table laid out with no address (never so with @requiresAddress). */
function topValue(table: Table): string | number | boolean {
  const top = table.cells[0]?.[0];
  return table.cornerEmpty || !top || top.v === null ? "" : top.v;
}

// --- Non-streaming layout functions --------------------------------------------
// Excel shares ONE streaming invocation between every cell whose argument
// values are identical, with the first cell's addresses (sideload 2026-10-24),
// so a function that writes outside its cell must not stream: TR, RHistory
// and the RDP.* names are plain functions, invoked once per cell. Their live
// updates "re-enter" the formula cell: Office.js range.calculate() on it,
// which re-invokes that cell alone (FormulaPort); a deleted or edited formula
// is found through worksheet change events (no onCanceled reaches a completed
// call).

/** One call of a non-streaming layout function, by key. */
interface Call {
  readonly key: string;
  readonly at: CellAt;
  readonly dest: CellAt | null;
  readonly signature: string;
  /** The function names a formula holding this call names (TR or RDP.Data, …). */
  readonly names: RegExp;
  /** The last write of ours (writer apply) or re-entry for it: the cache window starts there. */
  servedAt: number;
  /** Writer applies in flight (a re-invocation then always serves the cache). */
  writing: number;
  /**
   * The table last laid out and the formula value returned for it. Kept for
   * the call's life: a re-entry of its cell for a live sibling call re-invokes
   * it at any time, and is served from here.
   */
  shown: { table: Table; value: any } | null;
  /** The build in flight: another invocation awaits it, never refetches. */
  building: Promise<Built> | null;
  /** Pending end of a call no longer in its formula (cancelled by an invocation). */
  endTimer: ReturnType<typeof setTimeout> | null;
  /** Marked by a refresh command: the next invocation fetches again, past the cache and a live session (refresh). */
  refetch: boolean;
}

/** Non-streaming calls by key, and the keys of each formula cell (cellId). */
const calls = new Map<string, Call>();
const callsByCell = new Map<string, Set<Call>>();

function addCall(call: Call): void {
  calls.set(call.key, call);
  const id = cellId(call.at);
  if (!callsByCell.has(id)) callsByCell.set(id, new Set());
  callsByCell.get(id)!.add(call);
}

function removeCall(call: Call): void {
  if (calls.get(call.key) !== call) return;
  calls.delete(call.key);
  const id = cellId(call.at);
  const set = callsByCell.get(id);
  set?.delete(call);
  if (set && !set.size) callsByCell.delete(id);
}

/**
 * A same-signature re-invocation outside a live session serves the table
 * already laid out, without refetching, when it comes within RELEASE_GRACE_MS
 * of a write of ours or a re-entry for the call (or while a write is in
 * flight): it is then Excel's answer to that write (a Dest argument, or a
 * cell the formula reads, changed) or re-entry, and refetching would loop.
 * Any other re-invocation (a full recalculation, a precedent edited back to
 * the same value later) refetches.
 */
function servesCache(call: Call): boolean {
  return call.shown !== null && (call.writing > 0 || Date.now() - call.servedAt <= RELEASE_GRACE_MS);
}

/** Start the call's cache window now (a write of ours, a re-entry of its cell). */
function touch(call: Call): void {
  call.servedAt = Date.now();
}

/**
 * Lay `table` out for `call`: the plan's block to the writer, the formula
 * value returned. A table already shown keeps the value it was shown with,
 * so `Updated at` only moves when the table does (golden tick_updated_at).
 * A real #N/A (RHistory NULL:NA gap) is thrown as the Excel error.
 */
function present(call: Call, table: Table): any {
  const plan = planOutput(table, call.at, call.dest);
  const value =
    call.shown && call.shown.table === table
      ? call.shown.value
      : plan.formulaIsError
        ? new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable)
        : plan.formulaValue;
  call.shown = { table, value };
  touch(call);
  call.writing++;
  getWriter()
    .apply(call.key, call.at.sheet, { row: call.at.row, col: call.at.col }, plan)
    .catch((e) => console.error("EAV: writing results failed", e))
    .then(() => {
      call.writing--;
      touch(call);
    });
  if (value instanceof CustomFunctions.Error) throw value;
  return value;
}

/** End `call` after RELEASE_GRACE_MS (its footprint cleared, its session ended) unless Excel invokes it again. */
function scheduleEnd(call: Call): void {
  getWriter().scheduleRelease(call.key);
  if (call.endTimer) clearTimeout(call.endTimer);
  call.endTimer = setTimeout(() => endCall(call), RELEASE_GRACE_MS);
}

/** Keep `call`: drop a pending scheduleEnd. */
function cancelEnd(call: Call): void {
  if (!call.endTimer) return;
  clearTimeout(call.endTimer);
  call.endTimer = null;
  getWriter().cancelRelease(call.key);
}

/** Forget `call` and end its live session (its footprint is released by the writer). */
function endCall(call: Call): void {
  if (call.endTimer) clearTimeout(call.endTimer);
  call.endTimer = null;
  removeCall(call);
  sessions.get(call.key)?.end();
}

const callsAt = (at: CellAt): Call[] => Array.from(callsByCell.get(cellId(at)) ?? []);

/** Sheets already reported gone (logged once each). */
const goneSheets = new Set<string>();

/**
 * The calls of a sheet that no longer exists (deleted, or renamed: keys hold
 * the name) end: their sessions now, their footprints after the grace period.
 * A renamed sheet's formulas come back as new calls when Excel recalculates
 * them; until then a live one does not tick (logged once per sheet).
 */
function endSheetCalls(sheet: string): void {
  const lower = sheet.toLowerCase();
  if (!goneSheets.has(lower)) {
    goneSheets.add(lower);
    console.warn(`EAV: sheet "${sheet}" no longer exists (deleted or renamed): its LSEG calls stop until recalculated`);
  }
  calls.forEach((c) => {
    if (c.at.sheet.toLowerCase() !== lower) return;
    sessions.get(c.key)?.end();
    scheduleEnd(c);
  });
}

/** A sheet was deleted: end the calls of every sheet that is gone. */
function onSheetDeleted(): void {
  getFormulaPort()
    .sheetNames()
    .then((names) => {
      const present = new Set(names.map((n) => n.toLowerCase()));
      const sheets = new Set(Array.from(calls.values()).map((c) => c.at.sheet));
      sheets.forEach((sheet) => present.has(sheet.toLowerCase()) || endSheetCalls(sheet));
    })
    .catch((e) => console.error("EAV: reading the sheets failed", e));
}

/**
 * Run a non-streaming LSEG-layout function: build its table, lay it out
 * (present) and return the formula-cell value. `destIndex` is the position of
 * the Dest argument (its address comes from `parameterAddresses`). The call
 * is keyed by its cell and `signature` (every argument but Dest's value, plus
 * Dest's address).
 * - Marked by a refresh command (refresh) and no build in flight: one
 *   fetch, past the cache window and the live session (which the new
 *   table's session replaces).
 * - Live session for the key: its latest table, no refetch (a tick's
 *   re-entry, or our Dest write re-invoking the formula).
 * - A build in flight for the key: awaited, so a re-invocation during a
 *   refetch (a probe's) answers with the new table, never the previous one.
 * - Within the cache window (servesCache): the table already shown.
 * - Otherwise a fetch.
 * A new key in a cell that has other calls (an argument or Dest edited)
 * probes the cell: calls no longer in its formula end and are released
 * (probeLater: at most one deferred probe per burst, so a volatile argument
 * cannot loop).
 */
async function layOutStatic(
  invocation: CustomFunctions.Invocation,
  family: LsegFamily,
  destIndex: number | null,
  signature: string,
  names: RegExp,
  build: () => Promise<Built>
): Promise<any> {
  const at = parseCellAddress(invocation.address);
  const dest = at ? destOf(at, destIndex, invocation.parameterAddresses) : null;
  // A Dest on another worksheet is refused before any backend call (spec
  // §5.3 rule 3, golden sideload_retest_lseg_vs_eav L!I13).
  const refused = at !== null && destOnOtherSheet(at, dest);
  // A formula value preview (CustomFunctionsRuntime 1.5) must not write.
  if (!at || invocation.isInValuePreview) return refused ? DEST_OTHER_SHEET : topValue((await settledBuild(build, family)).table);
  if (destIndex !== null) signature += `|${invocation.parameterAddresses?.[destIndex] ?? ""}`;
  const key = callKey(at, signature);
  // Invoked again: whatever release was pending for the key (formula edited,
  // cell probed) is cancelled.
  getWriter().cancelRelease(key);
  let call = calls.get(key);
  const isNew = !call;
  if (!call) {
    call = {
      key, at, dest, signature, names,
      servedAt: 0, writing: 0, shown: null, building: null, endTimer: null, refetch: false,
    };
    addCall(call);
    getFormulaPort().watch({ change: onSheetChange, sheetDeleted: onSheetDeleted });
  }
  cancelEnd(call);
  if (isNew && callsAt(at).length > 1) probeLater(at, "key");
  // Laid out as an empty table: the plan shows the refusal and writes nothing,
  // which also releases a block this call wrote on that sheet before.
  if (refused) return present(call, REFUSED);

  const c = call;
  // A refresh joins a build already in flight; the mark is used up either way.
  const refetch = c.refetch && !c.building;
  c.refetch = false;
  if (refetch) sessions.get(key)?.end();
  const session = sessions.get(key);
  if (session && session.signature === signature) return present(c, session.latest);
  if (!c.building && !refetch && servesCache(c)) return present(c, c.shown!.table);
  if (!c.building) {
    const p: Promise<Built> = settledBuild(build, family).then((b) => {
      if (c.building === p) c.building = null;
      return b;
    });
    c.building = p;
  }
  const { table, live } = await c.building;
  // Ended while fetching (the formula was deleted): nothing is written.
  if (calls.get(key) !== c) return topValue(table);
  if (live && !sessions.has(key)) new LiveSession(key, signature, table, live, { call: c });
  return present(c, sessions.get(key)?.latest ?? table);
}

// --- Probes: which calls are still in a cell's formula --------------------------

/**
 * Why a cell is probed: a new key in it ("key": the probe's re-invocations
 * are served from the cache) or the user's change event ("change": they
 * refetch what their window allows, so re-entering a formula by hand
 * refreshes it).
 */
type ProbeCause = "key" | "change";

/**
 * Whether a probe that several requests merged into serves the cache: only
 * if every one of them asked to. A user's change (serve=false) merged with a
 * new key's request is never turned into serve=true, so a refresh by hand is
 * never lost; the cost is at most one refetch of a sibling call.
 */
export function mergeServe(a: boolean, b: boolean): boolean {
  return a && b;
}

/** Cells to probe in the next turn; `deferred`: a probe put off to its window's end. */
const probing = new Map<string, { at: CellAt; serve: boolean; deferred: boolean }>();
let probeTimer: ReturnType<typeof setTimeout> | null = null;
/** When each cell was last probed, and whether by a deferred probe (cellId → …). */
const probedAt = new Map<string, { t: number; deferred: boolean }>();
/** Probes put off to the end of their cell's window (cellId → timer and merged serve flag). */
const deferredProbes = new Map<string, { timer: ReturnType<typeof setTimeout>; serve: boolean }>();

/**
 * Probe `at` soon; the cells probed in one turn share one re-entry.
 * - A cell probed within RELEASE_GRACE_MS is probed once more at the end of
 *   that window (a key that came in between must still be checked), the
 *   requests in between merged into it (mergeServe).
 * - Except a new key inside the window of a DEFERRED probe: it comes from
 *   that probe's own re-entry (or with it), and is not probed again. A burst
 *   therefore has at most one deferred probe: a volatile argument (NOW(),
 *   RAND()) gives every re-entry's re-invocation a new key, and would
 *   otherwise re-probe, refetch and re-enter once a second forever. Its call
 *   then refetches only when Excel itself recalculates it.
 * - A user's change event is always probed (deferred when it must be): our
 *   own changes never reach here.
 */
function probeLater(at: CellAt, cause: ProbeCause): void {
  const id = cellId(at);
  const serve = cause === "key";
  const last = probedAt.get(id);
  const wait = last ? last.t + RELEASE_GRACE_MS - Date.now() : 0;
  if (wait > 0) {
    const pending = deferredProbes.get(id);
    if (pending) {
      pending.serve = mergeServe(pending.serve, serve);
      return;
    }
    if (cause === "key" && last!.deferred) return;
    const entry = {
      serve,
      timer: setTimeout(() => {
        deferredProbes.delete(id);
        queueProbe(at, entry.serve, true);
      }, wait + 1),
    };
    deferredProbes.set(id, entry);
    return;
  }
  queueProbe(at, serve, false);
}

function queueProbe(at: CellAt, serve: boolean, deferred: boolean): void {
  const id = cellId(at);
  const prev = probing.get(id);
  probing.set(id, { at, serve: prev ? mergeServe(prev.serve, serve) : serve, deferred: deferred || !!prev?.deferred });
  if (!probeTimer) probeTimer = setTimeout(runProbes, 0);
}

/**
 * Every call of a probed cell is scheduled to end, then the cell is
 * re-entered (recalculated): Excel re-invokes each call still in its formula,
 * which cancels its end (and is served from the cache). The calls of a cell
 * whose formula names none of their functions (deleted, overwritten) are not
 * re-invoked: they end after RELEASE_GRACE_MS. A cell that cannot be read or
 * recalculated tells nothing: its calls are kept.
 */
async function runProbes(): Promise<void> {
  probeTimer = null;
  const queued = Array.from(probing.values());
  probing.clear();
  const cells = queued.map((q) => q.at);
  const now = Date.now();
  probedAt.forEach((p, id) => now - p.t > RELEASE_GRACE_MS && probedAt.delete(id));
  queued.forEach((q) => probedAt.set(cellId(q.at), { t: now, deferred: q.deferred }));
  const groups = cells.map(callsAt);
  groups.forEach((g, i) => g.forEach((c) => (scheduleEnd(c), queued[i].serve && touch(c))));
  let found: Reentry[];
  try {
    found = await getFormulaPort().reenter(cells);
  } catch (e) {
    console.error("EAV: probing formula cells failed", e);
    found = cells.map(() => ({ formula: null, reentered: false }));
  }
  found.forEach((r, i) => {
    if (r.sheetGone) endSheetCalls(cells[i].sheet);
    else if (r.formula === null) groups[i].forEach(cancelEnd);
  });
}

const contains = (r: SheetRect, at: CellAt): boolean =>
  r.sheet.toLowerCase() === at.sheet.toLowerCase() &&
  at.row >= r.row && at.row < r.row + r.rows && at.col >= r.col && at.col < r.col + r.cols;

/** A worksheet change we did not make: every tracked formula cell in it is probed (deleted, edited, overwritten). */
function onSheetChange(rect: SheetRect): void {
  callsByCell.forEach((set) => {
    const at = set.values().next().value!.at;
    if (contains(rect, at)) probeLater(at, "change");
  });
}

// --- Refresh commands (ribbon) ---------------------------------------------------

/**
 * LSEG's PLRefresh commands (golden refresh_out, 2026-10-26): every EAV
 * formula cell in `scope` is fetched again and shows a new `Updated at`, TR
 * and RHistory alike; no other formula is recalculated. The cells are found
 * with one batched `formulas` read (FormulaPort.formulasIn), their calls
 * marked "refetch once" (layOutStatic), then recalculated in one batch
 * (range.calculate(), FormulaPort.reenter): Excel re-invokes each, and the
 * fetches go through the usual request limits (lib/batcher). A call whose
 * build is in flight is not marked: its re-invocation joins that build. A
 * cell not recalculated (refused) loses its marks. Returns the number of
 * cells recalculated.
 */
export async function refresh(scope: RefreshScope): Promise<number> {
  const port = getFormulaPort();
  const seen = new Set<string>();
  const cells = (await port.formulasIn(scope)).filter((f) => {
    if (!EAV_FORMULA.test(f.formula) || seen.has(cellId(f))) return false;
    seen.add(cellId(f));
    return true;
  });
  if (!cells.length) return 0;
  cells.forEach((at) => callsAt(at).forEach((c) => (c.refetch = !c.building)));
  const found = await port.reenter(cells);
  found.forEach((r, i) => {
    if (r.reentered) return;
    callsAt(cells[i]).forEach((c) => (c.refetch = false));
    if (r.sheetGone) endSheetCalls(cells[i].sheet);
  });
  return found.filter((r) => r.reentered).length;
}

/** What a refresh command covers: the selection (Refresh), the active sheet, or the workbook. */
export type RefreshScope = FormulaScope;

// --- Streaming layout (RtGet) ----------------------------------------------------

/**
 * Run a streaming LSEG-layout function (RtGet, scalar: Excel sharing one
 * invocation between identical cells is harmless there). As layOutStatic,
 * but the value goes through `setResult`, every tick included. Cancelling
 * (formula deleted, edited or re-invoked) schedules the release of the call's
 * footprint, which a re-invocation of the same call cancels; a result
 * arriving after cancelling is dropped. A result with realtime cells then
 * stays live (LiveSession).
 */
function layOutStream(
  invocation: Invocation,
  family: LsegFamily,
  signature: string,
  build: () => Promise<Built>
): void {
  const at = parseCellAddress(invocation.address);
  const key = at ? callKey(at, signature) : null;
  // A new invocation of this call keeps the footprint.
  if (key) getWriter().cancelRelease(key);

  // The same call re-invoked while live (a probe or a recalculation): carry
  // on with the same session, without refetching or resubscribing. Another
  // call of the same cell has another key: its session is never touched here.
  const session = key ? sessions.get(key) : undefined;
  if (session && session.signature === signature) {
    session.attach(invocation);
    return;
  }

  let cancelled = false;
  invocation.onCanceled = () => {
    cancelled = true;
    // Deferred: a re-invocation follows a cancel (writer RELEASE_GRACE_MS).
    // A deleted formula is cleared after it.
    if (key) getWriter().scheduleRelease(key);
  };

  settledBuild(build, family)
    .then(({ table, live }) => {
      if (cancelled) return;
      if (!at || !key) {
        invocation.setResult(topValue(table));
        return;
      }
      const formula = { sheet: at.sheet, row: at.row, col: at.col };
      const show = (targets: Invocation[], t: Table) => {
        const plan = planOutput(t, formula, null);
        const value = plan.formulaIsError ? new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable) : plan.formulaValue;
        targets.forEach((target) => target.setResult(value));
        getWriter()
          .apply(key, at.sheet, { row: at.row, col: at.col }, plan)
          .catch((e) => console.error("EAV: writing results failed", e));
      };
      show([invocation], table);
      if (!live) return;
      // Two builds of one call ran at once (identical calls in one cell, or a
      // re-invocation before the first build settled): the later one joins
      // the session the first opened, so none is left subscribed unowned.
      const running = sessions.get(key);
      if (running && running.signature === signature) running.attach(invocation, false);
      else new LiveSession(key, signature, table, live, { show }, invocation);
    })
    .catch((e) => console.error("EAV: laying out results failed", e));
}

// --- Live sessions (spec §3a) --------------------------------------------------

/** At most one live update per formula per LIVE_THROTTLE_MS (golden tick_fx_crypto: in lock-step with RtGet). */
const LIVE_THROTTLE_MS = 300;

/** Live sessions by call key (`Sheet!A1#<callId>`). */
const sessions = new Map<string, LiveSession>();

/** End every live session and forget every call (tests). */
export function endLiveSessions(): void {
  Array.from(sessions.values()).forEach((s) => s.end());
  Array.from(calls.values()).forEach(endCall);
  dirty.clear();
  reentryFlush.cancel();
  probing.clear();
  probedAt.clear();
  deferredProbes.forEach((d) => clearTimeout(d.timer));
  deferredProbes.clear();
  goneSheets.clear();
  if (probeTimer) clearTimeout(probeTimer);
  probeTimer = null;
}

/**
 * How a session shows a tick: a streaming call sets the result of its
 * invocations (`show`); a non-streaming one re-enters its formula (`call`).
 */
type LiveOutput = { show: (targets: Invocation[], t: Table) => void } | { call: Call };

/**
 * A call's realtime cells subscribed on the shared stream (spec §3a). Each
 * tick rebuilds the table (`latest`) and shows it, throttled; the writer
 * skips an unchanged plan.
 * - Streaming (RtGet): the value goes to every attached invocation. The
 *   session outlives its invocations: a re-invocation with the same
 *   `signature` re-attaches; with none attached for RELEASE_GRACE_MS
 *   (formula deleted or edited) it ends and unsubscribes.
 * - Non-streaming (TR, RDP.Data, RDP.Price): the formula cell's formula is
 *   re-entered (reenterLive), and Excel's re-invocation returns `latest`.
 *   The session ends with its call (probe, change event).
 * A per-RIC status (unknown RIC) or a lost connection leaves the cells as
 * they are.
 */
class LiveSession {
  private readonly targets = new Set<Invocation>();
  latest: Table;
  private alive = true;
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly flush: { call: () => void; cancel: () => void };
  private readonly handle: { stop: () => void };

  constructor(
    readonly key: string,
    readonly signature: string,
    table: Table,
    live: LiveFeed,
    readonly output: LiveOutput,
    invocation?: Invocation
  ) {
    this.latest = table;
    sessions.set(key, this);
    this.flush = "show" in output ? throttle(() => this.render(), LIVE_THROTTLE_MS) : { call: () => reenterLive(this), cancel: () => dirty.delete(this) };
    this.handle = openRtStream(
      live.rics,
      live.fields,
      (ric, fields) => {
        const t = live.tick(ric, fields);
        if (!t) return;
        this.latest = t;
        this.flush.call();
      },
      (message, fatal, ric) => {
        // The cells keep their values; the failure is only logged.
        if (!ric) console.warn(`EAV: live stream ${fatal ? "stopped" : "error"}: ${message}`);
      }
    );
    if (invocation) this.attach(invocation, false);
  }

  get isAlive(): boolean {
    return this.alive;
  }

  /**
   * Add a streaming `invocation` to the formula's result targets. By default
   * it is shown the latest table at once (a re-invocation's own result is
   * otherwise empty); the writer skips the unchanged block, so this cannot loop.
   */
  attach(invocation: Invocation, showNow = true): void {
    if (this.endTimer) clearTimeout(this.endTimer);
    this.endTimer = null;
    this.targets.add(invocation);
    invocation.onCanceled = () => this.detach(invocation);
    if (showNow && "show" in this.output) this.output.show([invocation], this.latest);
  }

  private render(): void {
    if (!this.targets.size || !("show" in this.output)) return; // between a cancel and a re-invocation: shown on attach
    this.output.show(Array.from(this.targets), this.latest);
  }

  private detach(invocation: Invocation): void {
    if (!this.targets.delete(invocation) || this.targets.size) return;
    // A formula re-invoked right away keeps its footprint (writer RELEASE_GRACE_MS).
    getWriter().scheduleRelease(this.key);
    this.endTimer = setTimeout(() => this.end(), RELEASE_GRACE_MS);
  }

  /** Unsubscribe and forget the session. */
  end(): void {
    this.alive = false;
    if (this.endTimer) clearTimeout(this.endTimer);
    this.endTimer = null;
    this.flush.cancel();
    this.handle.stop();
    this.targets.clear();
    if (sessions.get(this.key) === this) sessions.delete(this.key);
  }
}

// --- Live re-entry --------------------------------------------------------------

/** Non-streaming sessions with a tick not yet shown. */
const dirty = new Set<LiveSession>();
const reentryFlush = throttle(() => {
  reentering = reentering.then(flushReentries, flushReentries);
}, LIVE_THROTTLE_MS);
let reentering: Promise<void> = Promise.resolve();

/** Show a tick of a non-streaming session: its cell is re-entered at the next flush (at most one per LIVE_THROTTLE_MS). */
function reenterLive(session: LiveSession): void {
  dirty.add(session);
  reentryFlush.call();
}

/**
 * One Excel.run for every dirty session: each formula cell is recalculated
 * once (two live calls of one cell share it) and its formula read in the same
 * batch; Excel's re-invocation returns the session's latest table
 * (layOutStatic). A session whose formula no longer names its function ends,
 * and its call is released after the grace period.
 */
async function flushReentries(): Promise<void> {
  const batch = Array.from(dirty).filter((s) => s.isAlive && "call" in s.output);
  dirty.clear();
  if (!batch.length) return;
  const byCell = new Map<string, { at: CellAt; sessions: LiveSession[] }>();
  for (const s of batch) {
    const call = (s.output as { call: Call }).call;
    const id = cellId(call.at);
    if (!byCell.has(id)) byCell.set(id, { at: call.at, sessions: [] });
    byCell.get(id)!.sessions.push(s);
  }
  const groups = Array.from(byCell.values());
  // Every call of a re-entered cell is re-invoked, a non-live sibling too
  // (IF(TR(…,"DIVIDEND")=0,…,TR(…,"CF_YIELD"))): all are served from the cache.
  groups.forEach((g) => callsAt(g.at).forEach(touch));
  const callOf = (s: LiveSession) => (s.output as { call: Call }).call;
  let found: Reentry[];
  try {
    found = await getFormulaPort().reenter(groups.map((g) => g.at));
  } catch (e) {
    console.error("EAV: live update failed", e);
    return;
  }
  found.forEach((r, i) => {
    if (r.sheetGone) return endSheetCalls(groups[i].at.sheet);
    if (r.formula === null) return;
    const formula = r.formula;
    groups[i].sessions.forEach((s) => {
      if (callOf(s).names.test(formula)) return;
      s.end();
      scheduleEnd(callOf(s));
    });
  });
}

// --- Functions -------------------------------------------------------------

// The names a formula calls each non-streaming function by (any namespace:
// =EAV.TR, =EAVDEV.RDP.Data). Its live re-entry checks the cell still does.
const TR_NAMES = /\b(?:TR|RDP\.DATA)\s*\(/i;
const RHISTORY_NAMES = /\b(?:RHISTORY|RDP\.HISTORICALPRICING)\s*\(/i;
const RDP_PRICE_NAMES = /\bRDP\.PRICE\s*\(/i;
const RDP_SEARCH_NAMES = /\bRDP\.SEARCH\s*\(/i;
const AGGREGATE_NAMES = /\b(?:TRAGGREGATE|RDP\.AGGREGATE)\s*\(/i;
const ADFIN_DATE_NAMES = /\bRDP\.DATE(?:ISWD|ADJTOWD|LASTWD|LISTHOLIDAYS|CALCPERIOD|COUNTPERIODS|FINDDATE)\s*\(/i;
const ADFIN_FX_NAMES = /\bRDP\.FX(?:SPOT|SWPTOSWP|SWPTODEP|DEPTOSWP|ZCTOFRA|INFO)\s*\(/i;
const ADFIN_INTERP_NAMES = /\bRDP\.INTERP\s*\(/i;
const ANALYTICS_NAMES = /\bRDP\.ANALYTICS\s*\(/i;

/**
 * Refinitiv DataGrid — fundamentals, ratios, descriptive fields, realtime
 * fields and calculated time-series, laid out as LSEG's TR: the formula cell
 * holds the top-left value (or `Updated at HH:MM:SS`), the rest is written as
 * values. One row per instrument in input order; CH=Fd for a header row,
 * RH=IN / RH=Date for row headers, TRANSPOSE=Y, NULL=x. Not streaming (see
 * layOutStatic): realtime fields update by recalculating the formula cell.
 * @customfunction TR
 * @requiresAddress
 * @requiresParameterAddresses
 * @param universe Universe: RICs as a cell range, or a comma/semicolon-separated string (e.g. "VOD.L,GOOG.O").
 * @param dataItem Data Item: fields as a cell range, or a semicolon-separated string (e.g. "TR.Revenue;TR.NetIncome").
 * @param [options] Options: Key=Value string (e.g. "SDate=2023-01-01 EDate=0D Frq=FY CH=Fd RH=IN").
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param [arg1] Arg 1: value substituted for #1 in the other arguments.
 * @param [arg2] Arg 2: value substituted for #2.
 * @param [arg3] Arg 3: value substituted for #3.
 * @param [arg4] Arg 4: value substituted for #4.
 * @param [arg5] Arg 5: value substituted for #5.
 * @param [arg6] Arg 6: value substituted for #6.
 * @param [arg7] Arg 7: value substituted for #7.
 * @param [arg8] Arg 8: value substituted for #8.
 * @param [arg9] Arg 9: value substituted for #9.
 * @param [arg10] Arg 10: value substituted for #10.
 * @param [arg11] Arg 11: value substituted for #11.
 * @param [arg12] Arg 12: value substituted for #12.
 * @param [arg13] Arg 13: value substituted for #13.
 * @param [arg14] Arg 14: value substituted for #14.
 * @param [arg15] Arg 15: value substituted for #15.
 * @param [arg16] Arg 16: value substituted for #16.
 * @param [arg17] Arg 17: value substituted for #17.
 * @param [arg18] Arg 18: value substituted for #18.
 * @param [arg19] Arg 19: value substituted for #19.
 * @param [arg20] Arg 20: value substituted for #20.
 * @param invocation Invocation (provided by Excel).
 */
export function TR(
  universe: any[][],
  dataItem: any[][],
  options: any,
  dest: any,
  arg1: any, arg2: any, arg3: any, arg4: any, arg5: any, arg6: any, arg7: any, arg8: any, arg9: any, arg10: any,
  arg11: any, arg12: any, arg13: any, arg14: any, arg15: any, arg16: any, arg17: any, arg18: any, arg19: any, arg20: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  void dest;
  const args = [arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9, arg10, arg11, arg12, arg13, arg14, arg15, arg16, arg17, arg18, arg19, arg20];
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify(["TR", universe, dataItem, options, args]);
  return layOutStatic(invocation, "TR", 3, signature, TR_NAMES, () =>
    trLive(substituteArgs(universe, args), substituteArgs(dataItem, args), substituteArgs(options, args))
  );
}

/**
 * Historical pricing time-series (LSEG's RHistory), laid out as LSEG does:
 * the formula cell holds the top-left value (or `Updated at HH:MM:SS` with a
 * Dest elsewhere), the rest is written as values. Newest first by default.
 * e.g. =EAV.RHISTORY(A2, ".Timestamp;.Close", "START:2024-01-01 END:0D INTERVAL:1D", , "SORT:ASC CH:Fd").
 * The EAV positional form is EAV.HISTORY.
 * @customfunction RHISTORY
 * @requiresAddress
 * @requiresParameterAddresses
 * @param instruments Instruments: RICs as a cell range, or a comma/semicolon-separated string.
 * @param [fields] Fields (e.g. ".Timestamp;.Close").
 * @param [request] Request: "START:… END:… INTERVAL:1D NBROWS:10".
 * @param [refresh] Refresh: empty or "FRQ:1D"; checked as LSEG does, no automatic refresh.
 * @param [display] Display: "SORT:ASC CH:Fd".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RHISTORY(
  instruments: any[][],
  fields: any[][],
  request: any,
  refresh: any,
  display: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  void dest;
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify(["RHISTORY", instruments, fields, request, refresh, display]);
  return layOutStatic(invocation, "RHISTORY", 5, signature, RHISTORY_NAMES, async () => ({
    table: await rhistoryTable(instruments, fields, request, refresh, display),
    live: null, // one-shot (spec §3a)
  }));
}

/**
 * Historical pricing, EAV positional form — spills a grid with a header row:
 * =EAV.HISTORY(ric, "TRDPRC_1", "daily", "2024-01-01", "0D", 50).
 * @customfunction HISTORY
 * @param instruments RICs: a cell range, or a comma/semicolon-separated string.
 * @param fields Optional fields (e.g. "TRDPRC_1;BID").
 * @param interval Optional interval ("daily", "1H").
 * @param start Optional start date.
 * @param end Optional end date.
 * @param count Optional max number of points.
 * @returns A time-series grid that spills into the worksheet.
 */
export async function HISTORY(
  instruments: any[][],
  fields?: any[][],
  interval?: any,
  start?: any,
  end?: any,
  count?: any
): Promise<any[][]> {
  return grid(() => historyGrid(instruments, fields, interval, start, end, count));
}

/**
 * Discovery / instrument search.
 * @customfunction RSEARCH
 * @param query Search text (e.g. "Apple").
 * @param view Optional search view (e.g. "EquityQuotes").
 * @param top Optional max number of hits (default 25).
 * @param filter Optional filter expression.
 * @returns A grid of matching instruments.
 */
export async function RSEARCH(query: string, view?: string, top?: number, filter?: string): Promise<any[][]> {
  return run(buildCall("RSearch", [q(query), q(view), n(top), q(filter)]));
}

/**
 * Realtime price (LSEG's RtGet), positional: =EAV.RTGET(source, instrument,
 * field, [request]), e.g. =EAV.RTGET("IDN", "XS3126373912=BBVA", "BID"). The
 * value goes in the formula cell. The source (e.g. "IDN") is accepted for
 * Excel compatibility; the backend uses the shared hub feed. As in LSEG,
 * there is no 2-arg form: =EAV.RTGET("VOD.L", "BID") reads VOD.L as the
 * source and shows "#N/A No field defined.". Several RICs at once: EAV.RTGETS.
 * Streaming: its value is the formula cell alone, so Excel sharing one
 * invocation between identical cells is harmless.
 * @customfunction RTGET
 * @streaming
 * @requiresAddress
 * @param source Source: feed/service name (e.g. "IDN").
 * @param [instrument] Instrument: the RIC (a range reads its first cell).
 * @param [field] Field (e.g. "BID"; a range reads its first cell).
 * @param [request] Request: LSEG request parameters — accepted for compatibility, ignored.
 * @param invocation Invocation (provided by Excel).
 */
export function RTGET(
  source: any,
  instrument: any[][],
  field: any[][],
  request: any,
  invocation: CustomFunctions.StreamingInvocation<any>
): void {
  void request;
  const signature = JSON.stringify(["RTGET", source, instrument, field, request]);
  layOutStream(invocation, "RTGET", signature, () => rtgetLive(source, instrument, field));
}

/**
 * Realtime prices for several RICs and fields — spills an Instrument-headed
 * grid, one row per RIC: =EAV.RTGETS("IDN", "VOD.L,AAPL.O", "BID;ASK").
 * @customfunction RTGETS
 * @param source Feed/service name (e.g. "IDN").
 * @param instruments One or more RICs, comma- or semicolon-separated.
 * @param fields Fields, comma- or semicolon-separated (e.g. "BID;ASK;TRDPRC_1").
 * @param request Optional LSEG request parameters — accepted for compatibility, ignored.
 * @returns A realtime grid (one row per RIC).
 */
export async function RTGETS(source: string, instruments?: string, fields?: string, request?: string): Promise<any[][]> {
  void request;
  return grid(() => rtgetGrid(source, instruments, fields));
}

/**
 * Instrument metadata — name, exchange, currency, region, ISIN.
 * @customfunction METADATA
 * @param ric A single RIC (e.g. "VOD.L").
 * @returns A two-column field/value grid.
 */
export async function METADATA(ric: string): Promise<any[][]> {
  return run(buildCall("Metadata", [q(ric)]));
}

/**
 * Company overview — business summary, sector, employees, IPO date.
 * @customfunction OVERVIEW
 * @param ric A single RIC (e.g. "VOD.L").
 * @returns A two-column field/value grid.
 */
export async function OVERVIEW(ric: string): Promise<any[][]> {
  return run(buildCall("Overview", [q(ric)]));
}

/**
 * Latest news headlines for a RIC.
 * @customfunction NEWS
 * @param ric A single RIC (e.g. "VOD.L").
 * @param count Optional number of headlines (default 10).
 * @returns A grid of headlines (timestamp, headline, source, urgency, storyId).
 */
export async function NEWS(ric: string, count?: number): Promise<any[][]> {
  return run(buildCall("News", [q(ric), n(count)]));
}

/**
 * Live, continuously-updating realtime prices (streaming). The cell spills a
 * grid (one row per RIC) and refreshes in place as ticks arrive — no manual
 * recalculation. Clear the cell to stop the stream.
 * @customfunction RTSTREAM
 * @param instruments One or more RICs, comma- or semicolon-separated (e.g. "VOD.L,AAPL.O").
 * @param fields Fields to show as columns, comma- or semicolon-separated (e.g. "BID;ASK;TRDPRC_1").
 * @param invocation Streaming invocation (provided by Excel).
 * @returns A live grid that updates as new ticks arrive.
 */
export function RTSTREAM(
  instruments: string,
  fields: string,
  invocation: CustomFunctions.StreamingInvocation<any[][]>
): void {
  const rics = splitList(instruments);
  const fieldList = splitList(fields);
  if (rics.length === 0) {
    invocation.setResult(new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "No instruments given."));
    return;
  }
  // Columns: the fields given, else discovered from the ticks.
  const cols = fieldList.slice();
  const latest: Record<string, Record<string, any>> = {};
  const grid = () => [
    ["Instrument", ...cols],
    ...rics.map((r) => [r, ...cols.map((c) => latest[r]?.[c] ?? "")]),
  ];
  const flush = throttle(() => invocation.setResult(grid()), 300);
  invocation.setResult(grid()); // blank grid until the first ticks
  const handle = openRtStream(
    rics,
    fieldList,
    (ric, fields) => {
      if (!fieldList.length) for (const k of Object.keys(fields)) if (!cols.includes(k)) cols.push(k);
      latest[ric] = { ...latest[ric], ...fields };
      flush.call();
    },
    (message, fatal, ric) => {
      if (ric) return; // one RIC's status: the others keep ticking
      const code = fatal ? CustomFunctions.ErrorCode.notAvailable : CustomFunctions.ErrorCode.invalidValue;
      invocation.setResult(new CustomFunctions.Error(code, message));
    }
  );
  invocation.onCanceled = () => {
    flush.cancel();
    handle.stop();
  };
}

/**
 * Body of a single news story by its story ID (from =LSEG.NEWS).
 * @customfunction STORY
 * @param storyId The story identifier.
 * @returns A two-column field/value grid with the story body.
 */
export async function STORY(storyId: string): Promise<any[][]> {
  return run(buildCall("Story", [q(storyId)]));
}

// --- Refinitiv Workspace rebrand aliases --------------------------------------
// Workspace renamed TR → RDP.Data, RHistory → RDP.HistoricalPricing and
// RtGet → RDP.Price; rebranded workbooks store the new names
// (`_xll.RDP.Data(...)`). Same arguments and output as the originals.

/**
 * Refinitiv Workspace name for TR — same arguments and output as EAV.TR.
 * @customfunction RDP.DATA
 * @requiresAddress
 * @requiresParameterAddresses
 * @param universe Universe: RICs as a cell range, or a comma/semicolon-separated string.
 * @param dataItem Data Item: fields as a cell range, or a semicolon-separated string.
 * @param [options] Options: Key=Value string (e.g. "Period=FY1 CH=Fd").
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param [arg1] Arg 1: value substituted for #1 in the other arguments.
 * @param [arg2] Arg 2: value substituted for #2.
 * @param [arg3] Arg 3: value substituted for #3.
 * @param [arg4] Arg 4: value substituted for #4.
 * @param [arg5] Arg 5: value substituted for #5.
 * @param [arg6] Arg 6: value substituted for #6.
 * @param [arg7] Arg 7: value substituted for #7.
 * @param [arg8] Arg 8: value substituted for #8.
 * @param [arg9] Arg 9: value substituted for #9.
 * @param [arg10] Arg 10: value substituted for #10.
 * @param [arg11] Arg 11: value substituted for #11.
 * @param [arg12] Arg 12: value substituted for #12.
 * @param [arg13] Arg 13: value substituted for #13.
 * @param [arg14] Arg 14: value substituted for #14.
 * @param [arg15] Arg 15: value substituted for #15.
 * @param [arg16] Arg 16: value substituted for #16.
 * @param [arg17] Arg 17: value substituted for #17.
 * @param [arg18] Arg 18: value substituted for #18.
 * @param [arg19] Arg 19: value substituted for #19.
 * @param [arg20] Arg 20: value substituted for #20.
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATA(
  universe: any[][],
  dataItem: any[][],
  options: any,
  dest: any,
  arg1: any, arg2: any, arg3: any, arg4: any, arg5: any, arg6: any, arg7: any, arg8: any, arg9: any, arg10: any,
  arg11: any, arg12: any, arg13: any, arg14: any, arg15: any, arg16: any, arg17: any, arg18: any, arg19: any, arg20: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return TR(
    universe, dataItem, options, dest,
    arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9, arg10,
    arg11, arg12, arg13, arg14, arg15, arg16, arg17, arg18, arg19, arg20,
    invocation
  );
}

/**
 * Refinitiv Workspace name for RHistory — same arguments and output as EAV.RHISTORY.
 * @customfunction RDP.HISTORICALPRICING
 * @requiresAddress
 * @requiresParameterAddresses
 * @param instruments Instruments: RICs as a cell range, or a comma/semicolon-separated string.
 * @param [fields] Fields (e.g. ".Timestamp;.Close").
 * @param [request] Request: "START:… END:… INTERVAL:1D".
 * @param [refresh] Refresh: empty or "FRQ:1D"; checked as LSEG does, no automatic refresh.
 * @param [display] Display: "SORT:ASC CH:Fd".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_HISTORICALPRICING(
  instruments: any[][],
  fields: any[][],
  request: any,
  refresh: any,
  display: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return RHISTORY(instruments, fields, request, refresh, display, dest, invocation);
}

/**
 * Refinitiv Workspace name for RtGet — same positional arguments and output as
 * EAV.RTGET. Not streaming, as the other layout functions: live values update
 * by recalculating the formula cell.
 * @customfunction RDP.PRICE
 * @requiresAddress
 * @param source Source: feed/service name (e.g. "IDN").
 * @param [instrument] Instrument: the RIC (a range reads its first cell).
 * @param [field] Field (e.g. "BID"; a range reads its first cell).
 * @param [request] Request: LSEG request parameters — accepted for compatibility, ignored.
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_PRICE(
  source: any,
  instrument: any[][],
  field: any[][],
  request: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  void request;
  const signature = JSON.stringify(["RDP.PRICE", source, instrument, field, request]);
  return layOutStatic(invocation, "RTGET", null, signature, RDP_PRICE_NAMES, () => rtgetLive(source, instrument, field));
}

/**
 * The table of one RDP.Search: the codes of the rows the hub returns (rowCodes: a Warrant document with several piped codes gives a row each),
 * in one column or, with TRANSPOSE, one row; a planning or search failure is
 * LSEG's text in a 1x1 table.
 */
async function rdpSearchTable(assetClass: any, criteria: any, request: any, display: any): Promise<Table> {
  const plan = planRdpSearch(assetClass, criteria, request, display);
  if ("error" in plan) return toTable([[plan.error]]);
  let result;
  try {
    const call = buildCall("RSearch", [q("*"), q(plan.view), n(plan.top), q(plan.filter), q(plan.orderBy), q(plan.select)]);
    // One request per cell: under the batcher's limit, retried when transient, never when refused.
    result = (await evaluateLimited(call, (e) => searchRefusal(e) !== null)).result;
  } catch (e) {
    return toTable([[searchFailureText(e)]]);
  }
  // The hub's columns are named as asked (RIC, ISIN, WarrantPipedRICs): read by name, any case.
  const wanted = new Map(["RIC", "ISIN", "WarrantPipedRICs"].map((c) => [c.toLowerCase(), c]));
  const names = (result.columns ?? []).map((c) => wanted.get(String(c).toLowerCase()) ?? String(c));
  const codes: string[] = [];
  for (const values of result.data ?? []) {
    const row: Record<string, unknown> = {};
    names.forEach((name, i) => (row[name] = values[i]));
    // Only a Warrant (plan.select) falls back to its piped RICs or ISIN; the others list RICs.
    codes.push(...rowCodes(row, plan.select !== undefined));
  }
  // NBROWS counts the rows written, not the documents.
  codes.length = Math.min(codes.length, plan.top);
  if (!codes.length) return toTable([[RDP_SEARCH_TEXT.NO_RESULTS]]);
  return toTable(plan.transpose ? [codes] : codes.map((c) => [c]));
}

/**
 * Refinitiv Workspace's RDP.Search: the RICs of an asset class matching a
 * search filter, laid out as LSEG does: one column from the formula cell (a
 * row with TRANSPOSE:Y), or from Dest with `Updated at HH:MM:SS` in the
 * formula cell. Errors are LSEG's texts in the formula cell. Not streaming
 * (see layOutStatic), one-shot.
 * e.g. =EAV.RDP.SEARCH("Equity", "TickerSymbol eq 'MC'", "NBROWS:10 SORT:MktCapCompanyUsd:desc").
 * @customfunction RDP.SEARCH
 * @requiresAddress
 * @requiresParameterAddresses
 * @param assetClass Asset Class: Equity, Bond, Fund, Option, Future or Warrant.
 * @param criteria Search Criteria: an LSEG Search filter (e.g. "TickerSymbol eq 'MC'"), or a cell holding it.
 * @param [requestParams] Request Parameters: "NBROWS:n" (default 25, maximum 2000) and "SORT:Field:asc|desc".
 * @param [displayParams] Display Parameters: "TRANSPOSE:Y" lays the RICs across one row.
 * @param [dest] Dest: destination cell; the RICs are written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_SEARCH(
  assetClass: any,
  criteria: any,
  requestParams: any,
  displayParams: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  void dest;
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify(["RDP.SEARCH", assetClass, criteria, requestParams, displayParams]);
  return layOutStatic(invocation, "TR", 4, signature, RDP_SEARCH_NAMES, async () => ({
    table: await rdpSearchTable(assetClass, criteria, requestParams, displayParams),
    live: null, // one-shot
  }));
}

/**
 * The table of one TRAggregate: the interface sums the data item in USD per
 * Group By value over the universe; the header row is its columns (the Group By
 * as typed, then an empty cell), then one row per group. No group gives the
 * header alone. A failed check or request is LSEG's text in a 1x1 table.
 */
async function aggregateTable(universe: any, dataItem: any, groupBy: any): Promise<Table> {
  const plan = planAggregate(dataItem, groupBy);
  if ("error" in plan) return toTable([[plan.error]]);
  const rics = flattenInstruments(universe).filter(Boolean);
  if (!rics.length) return toTable([[LSEG_TEXT.NO_UNIVERSE]]); // [G] batch25b agg_no_universe
  let result;
  try {
    const call = buildCall("TRAggregate", [q(rics.join(",")), q(plan.item), q(plan.groupBy)]);
    // One request per cell: under the batcher's limit, retried when transient, never when refused.
    result = (await evaluateLimited(call, (e) => searchRefusal(e) !== null)).result;
  } catch (e) {
    return toTable([[searchFailureText(e)]]);
  }
  const header = result.columns?.length ? result.columns : [plan.groupBy, ""];
  return toTable([header, ...(result.data ?? [])]);
}

/**
 * Refinitiv Workspace's TRAggregate: the sum of one data item (in USD unless
 * the item has its own Curn) per value of a Group By field, over a universe,
 * laid out as LSEG does: the header (the Group By, then an empty cell) from the
 * formula cell, then one row per group; from Dest, the formula cell shows
 * `Updated at HH:MM:SS`. Errors are LSEG's texts in the formula cell. Options
 * are accepted and ignored, as LSEG does. Not streaming (see layOutStatic),
 * one-shot.
 * e.g. =EAV.TRAGGREGATE("0#.FCHI", "TR.CompanyMarketCap", "TR.TRBCEconomicSector").
 * @customfunction TRAGGREGATE
 * @requiresAddress
 * @requiresParameterAddresses
 * @param universe Universe: RICs or a chain as a cell range, or a comma/semicolon-separated string (e.g. "0#.FCHI").
 * @param dataItem Data Item: the one field to sum (e.g. "TR.CompanyMarketCap").
 * @param groupBy Group By: the field whose values make the groups (e.g. "TR.TRBCEconomicSector").
 * @param [options] Options: accepted for compatibility, ignored.
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param [arg1] Arg 1: value substituted for #1.
 * @param [arg2] Arg 2: value substituted for #2.
 * @param [arg3] Arg 3: value substituted for #3.
 * @param [arg4] Arg 4: value substituted for #4.
 * @param [arg5] Arg 5: value substituted for #5.
 * @param [arg6] Arg 6: value substituted for #6.
 * @param [arg7] Arg 7: value substituted for #7.
 * @param [arg8] Arg 8: value substituted for #8.
 * @param [arg9] Arg 9: value substituted for #9.
 * @param [arg10] Arg 10: value substituted for #10.
 * @param [arg11] Arg 11: value substituted for #11.
 * @param [arg12] Arg 12: value substituted for #12.
 * @param [arg13] Arg 13: value substituted for #13.
 * @param [arg14] Arg 14: value substituted for #14.
 * @param [arg15] Arg 15: value substituted for #15.
 * @param [arg16] Arg 16: value substituted for #16.
 * @param [arg17] Arg 17: value substituted for #17.
 * @param [arg18] Arg 18: value substituted for #18.
 * @param [arg19] Arg 19: value substituted for #19.
 * @param [arg20] Arg 20: value substituted for #20.
 * @param invocation Invocation (provided by Excel).
 */
export function TRAGGREGATE(
  universe: any[][],
  dataItem: any[][],
  groupBy: any,
  options: any,
  dest: any,
  arg1: any, arg2: any, arg3: any, arg4: any, arg5: any, arg6: any, arg7: any, arg8: any, arg9: any, arg10: any,
  arg11: any, arg12: any, arg13: any, arg14: any, arg15: any, arg16: any, arg17: any, arg18: any, arg19: any, arg20: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  void dest;
  const args = [arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9, arg10, arg11, arg12, arg13, arg14, arg15, arg16, arg17, arg18, arg19, arg20];
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify(["TRAGGREGATE", universe, dataItem, groupBy, options, args]);
  return layOutStatic(invocation, "TR", 4, signature, AGGREGATE_NAMES, async () => ({
    table: await aggregateTable(substituteArgs(universe, args), substituteArgs(dataItem, args), substituteArgs(groupBy, args)),
    live: null, // one-shot
  }));
}

/**
 * Refinitiv Workspace name for TRAggregate — same arguments and output as EAV.TRAGGREGATE.
 * @customfunction RDP.AGGREGATE
 * @requiresAddress
 * @requiresParameterAddresses
 * @param universe Universe: RICs or a chain as a cell range, or a comma/semicolon-separated string (e.g. "0#.FCHI").
 * @param dataItem Data Item: the one field to sum (e.g. "TR.CompanyMarketCap").
 * @param groupBy Group By: the field whose values make the groups (e.g. "TR.TRBCEconomicSector").
 * @param [options] Options: accepted for compatibility, ignored.
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param [arg1] Arg 1: value substituted for #1.
 * @param [arg2] Arg 2: value substituted for #2.
 * @param [arg3] Arg 3: value substituted for #3.
 * @param [arg4] Arg 4: value substituted for #4.
 * @param [arg5] Arg 5: value substituted for #5.
 * @param [arg6] Arg 6: value substituted for #6.
 * @param [arg7] Arg 7: value substituted for #7.
 * @param [arg8] Arg 8: value substituted for #8.
 * @param [arg9] Arg 9: value substituted for #9.
 * @param [arg10] Arg 10: value substituted for #10.
 * @param [arg11] Arg 11: value substituted for #11.
 * @param [arg12] Arg 12: value substituted for #12.
 * @param [arg13] Arg 13: value substituted for #13.
 * @param [arg14] Arg 14: value substituted for #14.
 * @param [arg15] Arg 15: value substituted for #15.
 * @param [arg16] Arg 16: value substituted for #16.
 * @param [arg17] Arg 17: value substituted for #17.
 * @param [arg18] Arg 18: value substituted for #18.
 * @param [arg19] Arg 19: value substituted for #19.
 * @param [arg20] Arg 20: value substituted for #20.
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_AGGREGATE(
  universe: any[][],
  dataItem: any[][],
  groupBy: any,
  options: any,
  dest: any,
  arg1: any, arg2: any, arg3: any, arg4: any, arg5: any, arg6: any, arg7: any, arg8: any, arg9: any, arg10: any,
  arg11: any, arg12: any, arg13: any, arg14: any, arg15: any, arg16: any, arg17: any, arg18: any, arg19: any, arg20: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return TRAGGREGATE(
    universe, dataItem, groupBy, options, dest,
    arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9, arg10,
    arg11, arg12, arg13, arg14, arg15, arg16, arg17, arg18, arg19, arg20,
    invocation
  );
}

// --- RDP.Date*, RDP.Fx* and RDP.Interp (LSEG's AdFin functions) -----------------

/**
 * [A] An argument as LSEG's add-in receives it: a cell reference as a range,
 * a typed value as itself. Office.js hands a range parameter a matrix either
 * way, so a 1x1 matrix with no address (a typed value, or an expression) is
 * unwrapped; an omitted argument is null.
 */
function asLsegArg(value: unknown, address: string | undefined): unknown {
  if (value === undefined) return null;
  const single = Array.isArray(value) && value.length === 1 && Array.isArray(value[0]) && value[0].length === 1;
  return single && !address ? (value as unknown[][])[0][0] : value;
}

/**
 * The table of one RDP.Date*, RDP.Fx* or RDP.Interp call: LSEG's validation
 * text (no call), else LSEG's request sent through the interface's AdFin
 * pass-through, whose answer (hubPayload.adfin) is laid out as LSEG lays it
 * out. A failed request is its LSEG text in a 1x1 table. A function the
 * add-in answers itself (spec.local: Interp) sends nothing: its answer is
 * laid out the same way, or is a text of ours (`{ eavText }`) shown as is.
 */
async function adfinTable(fn: AdFinFn, args: unknown[]): Promise<Table> {
  const built = buildAdFinRequest(fn, args);
  const { local } = ADFIN_FUNCTIONS[fn];
  // [A] What a function answered here shows in the formula cell when it is an error, whatever its
  // Dest (golden interp_opt_error_dest). The date and FX functions are left as they were: not captured.
  const inFormulaCell = (table: Table): Table => (local ? { ...table, inFormulaCell: true } : table);
  if ("error" in built) return inFormulaCell(toTable([[built.error]]));
  if (local) {
    const answer = local(built.request) as { eavText?: unknown; error?: unknown } | null;
    if (typeof answer?.eavText === "string") return inFormulaCell(toTable([[answer.eavText]]));
    const table = layAdFinResponse(fn, args, answer);
    return answer?.error ? inFormulaCell(table) : table;
  }
  let response;
  try {
    const call = buildCall("AdFin", [q(built.endpoint), q(JSON.stringify(built.request))]);
    // One request per cell: under the batcher's limit, retried when transient, never when refused.
    response = await evaluateLimited(call, (e) => searchRefusal(e) !== null);
  } catch (e) {
    return toTable([[searchFailureText(e)]]);
  }
  return layAdFinResponse(fn, args, response.hubPayload?.adfin);
}

/**
 * Run one RDP.Date*, RDP.Fx* or RDP.Interp function: `values` are its Excel arguments in
 * LSEG's order (the function's own, Options, then Dest, LSEG's last argument).
 * Dest's value is never read (only its address, for the layout), so it is
 * passed on as null. Not streaming (see layOutStatic), one-shot: FX too, as
 * LSEG's non-streaming AdFin path (Ruling F1).
 */
function layOutAdFin(fn: AdFinFn, id: string, values: unknown[], invocation: CustomFunctions.Invocation): Promise<any> {
  const destIndex = ADFIN_FUNCTIONS[fn].argNames.length - 1;
  const names = fn === "Interp" ? ADFIN_INTERP_NAMES : fn in ADFIN_FX_FUNCTIONS ? ADFIN_FX_NAMES : ADFIN_DATE_NAMES;
  const addresses = invocation.parameterAddresses;
  const args = values.map((v, i) => (i === destIndex ? null : asLsegArg(v, addresses?.[i])));
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify([id, args]);
  return layOutStatic(invocation, "TR", destIndex, signature, names, async () => ({
    table: await adfinTable(fn, args),
    live: null, // one-shot
  }));
}

/**
 * Refinitiv Workspace's RDP.DateIsWD: whether a date is a working day in the
 * given calendars, laid out as LSEG does (IsWorkingDay, then the calendars).
 * e.g. =EAV.RDP.DATEISWD("2026-10-24", "EMU").
 * @customfunction RDP.DATEISWD
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [calculationDate] Calculation date: YYYY-MM-DD, a date cell, or "Calculation Date:YYYY-MM-DD".
 * @param [calendarCodes] Calendar codes: e.g. "EMU" or "EMU;FRA", or a row/column of cells.
 * @param [fields] Fields: IsWorkingDay, Calendars.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATEISWD(
  calculationDate: any[][],
  calendarCodes: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin("DateIsWD", "RDP.DATEISWD", [calculationDate, calendarCodes, fields, options, dest], invocation);
}

/**
 * Refinitiv Workspace's RDP.DateAdjToWD: a date moved to a working day by a
 * date moving convention, laid out as LSEG does.
 * e.g. =EAV.RDP.DATEADJTOWD("2026-10-24", "EMU", "ModifiedFollowing").
 * @customfunction RDP.DATEADJTOWD
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [calculationDate] Calculation date: YYYY-MM-DD, a date cell, or "Calculation Date:YYYY-MM-DD".
 * @param [calendarCodes] Calendar codes: e.g. "EMU" or "EMU;FRA", or a row/column of cells.
 * @param [dateMovingConvention] Date moving convention: e.g. ModifiedFollowing, NextBusinessDay, PreviousBusinessDay.
 * @param [fields] Fields: AdjustedDate.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATEADJTOWD(
  calculationDate: any[][],
  calendarCodes: any[][],
  dateMovingConvention: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "DateAdjToWD",
    "RDP.DATEADJTOWD",
    [calculationDate, calendarCodes, dateMovingConvention, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.DateLastWD: the last working day of the month of
 * a date, laid out as LSEG does.
 * e.g. =EAV.RDP.DATELASTWD("2026-10-24", "EMU").
 * @customfunction RDP.DATELASTWD
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [calculationDate] Calculation date: YYYY-MM-DD, a date cell, or "Calculation Date:YYYY-MM-DD".
 * @param [calendarCodes] Calendar codes: e.g. "EMU" or "EMU;FRA", or a row/column of cells.
 * @param [fields] Fields: LastWorkingDay.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATELASTWD(
  calculationDate: any[][],
  calendarCodes: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin("DateLastWD", "RDP.DATELASTWD", [calculationDate, calendarCodes, fields, options, dest], invocation);
}

/**
 * Refinitiv Workspace's RDP.DateListHolidays: the holidays between two dates
 * in the given calendars, one row each (date, name, calendars), laid out as LSEG does.
 * e.g. =EAV.RDP.DATELISTHOLIDAYS("2026-01-01", "2026-12-31", "EMU").
 * @customfunction RDP.DATELISTHOLIDAYS
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [startDate] Start date: YYYY-MM-DD or a date cell.
 * @param [endDate] End date: YYYY-MM-DD or a date cell.
 * @param [calendarCodes] Calendar codes: e.g. "EMU" or "EMU;FRA", or a row/column of cells.
 * @param [fields] Fields: Date, Name, Calendars.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATELISTHOLIDAYS(
  startDate: any[][],
  endDate: any[][],
  calendarCodes: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "DateListHolidays",
    "RDP.DATELISTHOLIDAYS",
    [startDate, endDate, calendarCodes, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.DateCalcPeriod: a date plus a period (a tenor such
 * as 3M or 1Y), in calendars or currencies, laid out as LSEG does (start and end dates).
 * e.g. =EAV.RDP.DATECALCPERIOD("2026-10-23", "3M", "EMU").
 * @customfunction RDP.DATECALCPERIOD
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [calculationDate] Calculation date: YYYY-MM-DD, a date cell, or "Calculation Date:YYYY-MM-DD".
 * @param [period] Period: a tenor (e.g. 3M, 1Y, 10WD, -1M, ON, 1x3, 2M/3M).
 * @param [calendarCodes] Calendar codes: e.g. "EMU" or "EMU;FRA" (not with currency codes).
 * @param [currencyCodes] Currency codes: e.g. "EUR" or "EUR;PLN" (not with calendar codes).
 * @param [fields] Fields: StartDate, EndDate.
 * @param [dateMovingConvention] Date moving convention: e.g. ModifiedFollowing.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATECALCPERIOD(
  calculationDate: any[][],
  period: any[][],
  calendarCodes: any[][],
  currencyCodes: any[][],
  fields: any[][],
  dateMovingConvention: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "DateCalcPeriod",
    "RDP.DATECALCPERIOD",
    [calculationDate, period, calendarCodes, currencyCodes, fields, dateMovingConvention, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.DateCountPeriods: the number of periods (working
 * days, days, years …) between two dates, laid out as LSEG does (count, then tenor).
 * e.g. =EAV.RDP.DATECOUNTPERIODS("2026-01-01", "2026-10-23", "EMU", "WorkingDay").
 * @customfunction RDP.DATECOUNTPERIODS
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [startDate] Start date: YYYY-MM-DD or a date cell.
 * @param [endDate] End date: YYYY-MM-DD or a date cell.
 * @param [calendarCodes] Calendar codes: mandatory for WorkingDay and NonWorkingDay.
 * @param [periodTypes] Period type: WorkingDay, NonWorkingDay, Day or Year.
 * @param [dayCountBasis] Day count basis (for Year): e.g. Dcb_Actual_360.
 * @param [fields] Fields: Count, Tenor.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATECOUNTPERIODS(
  startDate: any[][],
  endDate: any[][],
  calendarCodes: any[][],
  periodTypes: any[][],
  dayCountBasis: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "DateCountPeriods",
    "RDP.DATECOUNTPERIODS",
    [startDate, endDate, calendarCodes, periodTypes, dayCountBasis, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.DateFindDate: the n-th given weekday before or
 * after a reference date, laid out as LSEG does.
 * e.g. =EAV.RDP.DATEFINDDATE(1, "Monday", "After", "2026-10-23").
 * @customfunction RDP.DATEFINDDATE
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [dayNumber] Day number: e.g. 1 for the first.
 * @param [day] Day: Monday … Sunday.
 * @param [direction] Direction: Before or After.
 * @param [referenceDate] Reference date: YYYY-MM-DD or a date cell.
 * @param [fields] Fields: Date.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_DATEFINDDATE(
  dayNumber: any[][],
  day: any[][],
  direction: any[][],
  referenceDate: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "DateFindDate",
    "RDP.DATEFINDDATE",
    [dayNumber, day, direction, referenceDate, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxSpot: the spot cross rate of a currency pair,
 * from each currency's spot rate against the pivot currency, laid out as LSEG
 * does (spot date, then the bid and ask of each leg and of the cross).
 * e.g. =EAV.RDP.FXSPOT("EURUSD").
 * @customfunction RDP.FXSPOT
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [fxCrossCode] FX cross code: the currency pair, e.g. "EURUSD".
 * @param [marketDataDate] Market data date: YYYY-MM-DD or a date cell (default: now).
 * @param [spotCcy1] Spot of the base currency: source and bid/ask overrides, as text or a range.
 * @param [spotCcy2] Spot of the quoted currency: source and bid/ask overrides, as text or a range.
 * @param [fields] Fields: SpotDate, SpotCcy1Bid, SpotCcy1Ask, SpotCcy2Bid, SpotCcy2Ask, SpotCcy1Ccy2Bid, SpotCcy1Ccy2Ask.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXSPOT(
  fxCrossCode: any[][],
  marketDataDate: any[][],
  spotCcy1: any[][],
  spotCcy2: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "FxSpot",
    "RDP.FXSPOT",
    [fxCrossCode, marketDataDate, spotCcy1, spotCcy2, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxSwpToSwp: the swap points and outright rates of
 * a currency pair, one row per tenor, laid out as LSEG does.
 * e.g. =EAV.RDP.FXSWPTOSWP("EURUSD", , "1M;3M").
 * @customfunction RDP.FXSWPTOSWP
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [fxCrossCode] FX cross code: the currency pair, e.g. "EURUSD".
 * @param [marketDataDate] Market data date: YYYY-MM-DD or a date cell (default: now).
 * @param [tenors] Tenors: e.g. "1M;3M", end dates, or a row/column of cells.
 * @param [spotCcy1] Spot of the base currency: source and bid/ask overrides, as text or a range.
 * @param [spotCcy2] Spot of the quoted currency: source and bid/ask overrides, as text or a range.
 * @param [swapPointsCcy1] Swap points of the base currency: source and per-tenor overrides, as text or a range.
 * @param [swapPointsCcy2] Swap points of the quoted currency: source and per-tenor overrides, as text or a range.
 * @param [fields] Fields: e.g. Tenor, StartDate, EndDate, SwapCcy1Ccy2Bid, OutrightCcy1Ccy2Bid.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXSWPTOSWP(
  fxCrossCode: any[][],
  marketDataDate: any[][],
  tenors: any[][],
  spotCcy1: any[][],
  spotCcy2: any[][],
  swapPointsCcy1: any[][],
  swapPointsCcy2: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "FxSwpToSwp",
    "RDP.FXSWPTOSWP",
    [fxCrossCode, marketDataDate, tenors, spotCcy1, spotCcy2, swapPointsCcy1, swapPointsCcy2, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxSwpToDep: the deposit rates implied by a
 * currency pair's swap points, one row per tenor, laid out as LSEG does.
 * e.g. =EAV.RDP.FXSWPTODEP("EURUSD", , "1M;3M").
 * @customfunction RDP.FXSWPTODEP
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [fxCrossCode] FX cross code: the currency pair, e.g. "EURUSD".
 * @param [marketDataDate] Market data date: YYYY-MM-DD or a date cell (default: now).
 * @param [tenors] Tenors: e.g. "1M;3M", end dates, or a row/column of cells (default: ON to 2Y).
 * @param [spotCcy1] Spot of the base currency: source and bid/ask overrides, as text or a range.
 * @param [spotCcy2] Spot of the quoted currency: source and bid/ask overrides, as text or a range.
 * @param [swapPointsCcy1] Swap points of the base currency: source and per-tenor overrides, as text or a range.
 * @param [swapPointsCcy2] Swap points of the quoted currency: source and per-tenor overrides, as text or a range.
 * @param [depoRatesCcy1] Deposit rates of the base currency: source and per-tenor overrides, as text or a range.
 * @param [fields] Fields: e.g. Tenor, StartDate, EndDate, DepCcy1Bid, DepCcy2Bid.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXSWPTODEP(
  fxCrossCode: any[][],
  marketDataDate: any[][],
  tenors: any[][],
  spotCcy1: any[][],
  spotCcy2: any[][],
  swapPointsCcy1: any[][],
  swapPointsCcy2: any[][],
  depoRatesCcy1: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "FxSwpToDep",
    "RDP.FXSWPTODEP",
    [
      fxCrossCode,
      marketDataDate,
      tenors,
      spotCcy1,
      spotCcy2,
      swapPointsCcy1,
      swapPointsCcy2,
      depoRatesCcy1,
      fields,
      options,
      dest,
    ],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxDepToSwp: the swap points implied by the two
 * currencies' deposit rates, one row per tenor, laid out as LSEG does.
 * e.g. =EAV.RDP.FXDEPTOSWP("EURUSD", , "1M;3M").
 * @customfunction RDP.FXDEPTOSWP
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [fxCrossCode] FX cross code: the currency pair, e.g. "EURUSD".
 * @param [marketDataDate] Market data date: YYYY-MM-DD or a date cell (default: now).
 * @param [tenors] Tenors: e.g. "1M;3M", end dates, or a row/column of cells.
 * @param [spotCcy1] Spot of the base currency: source and bid/ask overrides, as text or a range.
 * @param [spotCcy2] Spot of the quoted currency: source and bid/ask overrides, as text or a range.
 * @param [depRatesCcy1] Deposit rates of the base currency: source and per-tenor overrides, as text or a range.
 * @param [depRatesCcy2] Deposit rates of the quoted currency: source and per-tenor overrides, as text or a range.
 * @param [fields] Fields: e.g. Tenor, StartDate, EndDate, SwapCcy1Ccy2Bid, OutrightCcy1Ccy2Bid.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXDEPTOSWP(
  fxCrossCode: any[][],
  marketDataDate: any[][],
  tenors: any[][],
  spotCcy1: any[][],
  spotCcy2: any[][],
  depRatesCcy1: any[][],
  depRatesCcy2: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "FxDepToSwp",
    "RDP.FXDEPTOSWP",
    [fxCrossCode, marketDataDate, tenors, spotCcy1, spotCcy2, depRatesCcy1, depRatesCcy2, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxZcToFra: the FRA rate between two dates from a
 * zero-coupon curve, laid out as LSEG does (start date, end date, FRA).
 * e.g. =EAV.RDP.FXZCTOFRA("EUR", "2026-11-23", "2027-02-23").
 * @customfunction RDP.FXZCTOFRA
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [zcCurve] Zero-coupon curve: a currency code (e.g. "EUR"), curve points as text, or a range.
 * @param [start] Start: YYYY-MM-DD, a date cell, or a tenor (e.g. 1M).
 * @param [end] End: YYYY-MM-DD, a date cell, or a tenor (e.g. 6M).
 * @param [calculationDate] Calculation date: YYYY-MM-DD or a date cell (default: today).
 * @param [dayCountBasis] Day count basis: e.g. Dcb_Actual_360.
 * @param [fields] Fields: StartDate, EndDate, Fra.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXZCTOFRA(
  zcCurve: any[][],
  start: any[][],
  end: any[][],
  calculationDate: any[][],
  dayCountBasis: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin(
    "FxZcToFra",
    "RDP.FXZCTOFRA",
    [zcCurve, start, end, calculationDate, dayCountBasis, fields, options, dest],
    invocation
  );
}

/**
 * Refinitiv Workspace's RDP.FxInfo: the conventions of each currency (name,
 * spot lags, calendar, quotation mode, year basis), one row per code, laid out as LSEG does.
 * e.g. =EAV.RDP.FXINFO("EUR;USD;JPY").
 * @customfunction RDP.FXINFO
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [currencyCodes] Currency codes: e.g. "EUR" or "EUR;USD;JPY", or a row/column of cells.
 * @param [fields] Fields: CurrencyCode, CurrencyName, FxSpotLag, MmSpotLag, Calendar, QuotationMode, MmYearBasis.
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the table is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_FXINFO(
  currencyCodes: any[][],
  fields: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin("FxInfo", "RDP.FXINFO", [currencyCodes, fields, options, dest], invocation);
}

/**
 * Refinitiv Workspace's RDP.Interp: the Y value at x on the points (X values,
 * Y values) by an interpolation method, laid out as LSEG does. Computed in the
 * add-in (nothing is sent to the backend). Lists are cell ranges (one row or
 * one column; blank pairs are skipped) or texts such as "1;2;4"; x may be a date.
 * e.g. =EAV.RDP.INTERP(5.5, A2:A7, B2:B7, "IM:LIN").
 * @customfunction RDP.INTERP
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [x] x: the value to interpolate at: a number, a date, or a cell.
 * @param [xValues] X values: a row or column of cells, or a text such as "1;2;4;7" (sorted, no 0).
 * @param [yValues] Y values: a row or column of cells, or a text such as "2;3.5;1;-0.5".
 * @param [method] Interpolation method: e.g. "IM:LIN".
 * @param [options] Options: e.g. "CH:Fd RH:Id TRANSPOSE:Y".
 * @param [dest] Dest: destination cell; the result is written there and the formula cell shows "Updated at".
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_INTERP(
  x: any[][],
  xValues: any[][],
  yValues: any[][],
  method: any[][],
  options: any,
  dest: any,
  invocation: CustomFunctions.Invocation
): Promise<any> {
  return layOutAdFin("Interp", "RDP.INTERP", [x, xValues, yValues, method, options, dest], invocation);
}

// --- RDP.Analytics (LSEG's QPS function; Types FinancialContracts, DatesAndCalendars, Curves, VolatilitySurface and Help) --------

/** RDP.Analytics' Dest: LSEG's 7th argument, before Arg 1…20 (QpsContract.argIndexMap.destinationCell). */
const ANALYTICS_DEST = 6;

/** A text EAV shows in the formula cell, whatever the Dest (as layAnalyticsResponse's own texts). */
const analyticsText = (text: string): Table => ({ ...toTable([[text]]), inFormulaCell: true });

/**
 * The table of one RDP.Analytics call: LSEG's validation text or EAV's not-available text (no
 * call), else IPA's answers to the request's batches, sent in parallel through the interface's
 * Analytics relay (hubPayload.adfin, Ruling R-A2) and laid out as LSEG lays them out.
 * - Every batch is awaited; if any failed, the formula shows the failure of the first one in
 *   batch order (LSEG sends one request: one failure fails the whole formula).
 * - Every text of ours or of a failure is in the formula cell whatever the Dest, as LSEG's
 *   ErrorHandler texts are (layAnalyticsResponse); the validation texts follow the AdFin
 *   precedent (golden interp_opt_error_dest): no analytics golden has a Dest with an error.
 * - A field IPA flags InvalidField that IPA's own field list spells otherwise is respelled and
 *   the batches sent once more (ipaFields.ts); if that fails, the first answers are laid out.
 */
async function analyticsTable(args: unknown[]): Promise<Table> {
  const built = buildAnalyticsRequest(args);
  if ("error" in built) return analyticsText(built.error);
  if ("notAvailable" in built) return analyticsText(built.notAvailable);
  if ("help" in built) return layAnalyticsResponse(args, []); // LSEG answers Help itself: no request
  if ("service" in built) {
    // LSEG posts DatesAndCalendars' and Curves' body in one request, with no batches and no field list.
    try {
      return layAnalyticsResponse(args, [await analyticsCall(built.service, built.body)]);
    } catch (e) {
      return analyticsText(analyticsErrorText(e));
    }
  }
  const sent = await sendAnalytics(built.batches);
  if ("failure" in sent) return analyticsText(analyticsErrorText(sent.failure));
  let answers = sent.answers;
  // Fields IPA flagged InvalidField that its own list spells otherwise: the request once more.
  const respelled = await respelledBatches(built, answers);
  if (respelled) {
    const again = await sendAnalytics(respelled);
    if ("answers" in again) answers = again.answers;
  }
  return layAnalyticsResponse(args, answers);
}

/** Every batch in parallel: their answers in batch order, or the first failure in batch order. */
async function sendAnalytics(batches: AnalyticsBody[]): Promise<{ answers: unknown[] } | { failure: unknown }> {
  // Each batch settles to its answer or its failure, so all are awaited before one is picked.
  const settled = await Promise.all(
    batches.map(async (body): Promise<{ answer: unknown } | { failure: unknown }> => {
      try {
        return { answer: await analyticsCall("financial-contracts", body) };
      } catch (e) {
        return { failure: e };
      }
    })
  );
  const failed = settled.find((s): s is { failure: unknown } => "failure" in s);
  return failed ?? { answers: settled.map((s) => ("answer" in s ? s.answer : null)) };
}

/** One request to the hub's analytics service `name` through the interface's Analytics relay: its answer (hubPayload.adfin). */
async function analyticsCall(name: string, body: unknown): Promise<unknown> {
  const call = buildCall("Analytics", [q(name), q(JSON.stringify(body))]);
  // Under the batcher's limit, retried when transient, never when refused.
  const response = await evaluateLimited(call, (e) => searchRefusal(e) !== null);
  return response.hubPayload?.adfin;
}

/**
 * The batches with their fields in IPA's spelling when IPA flagged one InvalidField and IPA's
 * field list for the first instrument spells it otherwise (fields newer than LSEG's metadata,
 * `cheapesttodeliver`), else null. The list is fetched once per subtype (and option underlying).
 */
async function respelledBatches(built: AnalyticsRequest, answers: unknown[]): Promise<AnalyticsBody[] | null> {
  if (built.getFields || !hasInvalidField(answers)) return null;
  const first = built.batches[0];
  const underlying = String(first.universe[0]?.instrumentDefinition?.underlyingType ?? "").toLowerCase();
  const names = await ipaFieldList(`${built.subType}|${underlying}`, async () =>
    fieldListNames(await analyticsCall("financial-contracts", fieldListRequest(first)))
  );
  const fields = names && respell(first.fields, names);
  return fields ? built.batches.map((b) => ({ ...b, fields })) : null;
}

/**
 * Refinitiv Workspace's RDP.Analytics: IPA analytics of a universe of instruments, laid out as
 * LSEG does. Types FinancialContracts (bond, swap, option, fx…), DatesAndCalendars, Curves, VolatilitySurface and Help ("About")
 * (AsyncSurfaces says it is not available yet). `#1`…`#20` in Universe, UniverseParams, Data Item and Options read Arg 1…20.
 * e.g. =EAV.RDP.ANALYTICS("FinancialContracts", "Bond", "FR10YT=RR", "", "CleanPrice;YieldPercent").
 * @customfunction RDP.ANALYTICS
 * @requiresAddress
 * @requiresParameterAddresses
 * @param [type] Type: the analytics type, e.g. "FinancialContracts".
 * @param [subtype] Subtype: the asset class within the Type, e.g. "Bond", "Swap", "FxForward".
 * @param [universe] Universe: instrument codes, or "Key:Value ..." definitions, separated by ";"; or cells.
 * @param [universeParameters] Universe parameters: e.g. "ValuationDate:2026-10-23", for all or per instrument.
 * @param [dataItem] Data Item: the fields, e.g. "CleanPrice;YieldPercent", or "getFields".
 * @param [options] Options: e.g. "RH:In CH:Fd TRANSPOSE:Y".
 * @param [destinationCell] Destination Cell: the result is written there and the formula cell shows "Updated at".
 * @param [arg1] Arg 1: value substituted for #1 in the other arguments.
 * @param [arg2] Arg 2: value substituted for #2.
 * @param [arg3] Arg 3: value substituted for #3.
 * @param [arg4] Arg 4: value substituted for #4.
 * @param [arg5] Arg 5: value substituted for #5.
 * @param [arg6] Arg 6: value substituted for #6.
 * @param [arg7] Arg 7: value substituted for #7.
 * @param [arg8] Arg 8: value substituted for #8.
 * @param [arg9] Arg 9: value substituted for #9.
 * @param [arg10] Arg 10: value substituted for #10.
 * @param [arg11] Arg 11: value substituted for #11.
 * @param [arg12] Arg 12: value substituted for #12.
 * @param [arg13] Arg 13: value substituted for #13.
 * @param [arg14] Arg 14: value substituted for #14.
 * @param [arg15] Arg 15: value substituted for #15.
 * @param [arg16] Arg 16: value substituted for #16.
 * @param [arg17] Arg 17: value substituted for #17.
 * @param [arg18] Arg 18: value substituted for #18.
 * @param [arg19] Arg 19: value substituted for #19.
 * @param [arg20] Arg 20: value substituted for #20.
 * @param invocation Invocation (provided by Excel).
 */
export function RDP_ANALYTICS(
  type: any[][],
  subtype: any[][],
  universe: any[][],
  universeParameters: any[][],
  dataItem: any[][],
  options: any,
  destinationCell: any,
  arg1: any[][], arg2: any[][], arg3: any[][], arg4: any[][], arg5: any[][],
  arg6: any[][], arg7: any[][], arg8: any[][], arg9: any[][], arg10: any[][],
  arg11: any[][], arg12: any[][], arg13: any[][], arg14: any[][], arg15: any[][],
  arg16: any[][], arg17: any[][], arg18: any[][], arg19: any[][], arg20: any[][],
  invocation: CustomFunctions.Invocation
): Promise<any> {
  const values = [
    type, subtype, universe, universeParameters, dataItem, options, destinationCell,
    arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9, arg10,
    arg11, arg12, arg13, arg14, arg15, arg16, arg17, arg18, arg19, arg20,
  ];
  // As layOutAdFin: a cell reference stays a range (LSEG's add-in joins a range's cells), a typed
  // value is unwrapped, an omitted argument is null; Dest's value is never read, so it is null.
  const addresses = invocation.parameterAddresses;
  const args = values.map((v, i) => (i === ANALYTICS_DEST ? null : asLsegArg(v, addresses?.[i])));
  // Every argument but Dest, whose value our own writes change.
  const signature = JSON.stringify(["RDP.ANALYTICS", args]);
  return layOutStatic(invocation, "TR", ANALYTICS_DEST, signature, ANALYTICS_NAMES, async () => ({
    table: await analyticsTable(args),
    live: null, // one-shot
  }));
}

// Explicit associations — robust regardless of how the bundle is loaded. The
// id (uppercased function name) matches the @customfunction id the metadata
// generator emits, and the manifest namespace prefixes them as =EAV.*.
const FUNCTIONS: [string, (...args: any[]) => unknown][] = [
  ["TR", TR],
  ["RHISTORY", RHISTORY],
  ["HISTORY", HISTORY],
  ["RSEARCH", RSEARCH],
  ["RTGET", RTGET],
  ["RTGETS", RTGETS],
  ["METADATA", METADATA],
  ["OVERVIEW", OVERVIEW],
  ["NEWS", NEWS],
  ["STORY", STORY],
  ["RTSTREAM", RTSTREAM],
  ["RDP.DATA", RDP_DATA],
  ["RDP.HISTORICALPRICING", RDP_HISTORICALPRICING],
  ["RDP.PRICE", RDP_PRICE],
  ["RDP.SEARCH", RDP_SEARCH],
  ["TRAGGREGATE", TRAGGREGATE],
  ["RDP.AGGREGATE", RDP_AGGREGATE],
  ["RDP.DATEISWD", RDP_DATEISWD],
  ["RDP.DATEADJTOWD", RDP_DATEADJTOWD],
  ["RDP.DATELASTWD", RDP_DATELASTWD],
  ["RDP.DATELISTHOLIDAYS", RDP_DATELISTHOLIDAYS],
  ["RDP.DATECALCPERIOD", RDP_DATECALCPERIOD],
  ["RDP.DATECOUNTPERIODS", RDP_DATECOUNTPERIODS],
  ["RDP.DATEFINDDATE", RDP_DATEFINDDATE],
  ["RDP.FXSPOT", RDP_FXSPOT],
  ["RDP.FXSWPTOSWP", RDP_FXSWPTOSWP],
  ["RDP.FXSWPTODEP", RDP_FXSWPTODEP],
  ["RDP.FXDEPTOSWP", RDP_FXDEPTOSWP],
  ["RDP.FXZCTOFRA", RDP_FXZCTOFRA],
  ["RDP.FXINFO", RDP_FXINFO],
  ["RDP.INTERP", RDP_INTERP],
  ["RDP.ANALYTICS", RDP_ANALYTICS],
];
FUNCTIONS.forEach(([id, fn]) => CustomFunctions.associate(id, fn));

/**
 * A formula calling any function of the add-in, in any case: in its namespace
 * as Excel shows it (=EAV.TR(, =EAV.RDP.Data(; the dev manifest's EAVDEV [A])
 * or as a workbook stores it without the add-in (_xldudf_EAV_RDP_DATA( [A]).
 * LSEG's own =TR( and any other formula never match.
 */
const EAV_NAMES = FUNCTIONS.map(([id]) => id.replace(/\./g, "[._]")).join("|");
const EAV_FORMULA = new RegExp(String.raw`(?:^|[^\w.])(?:_xldudf_)?EAV(?:DEV)?[._](?:${EAV_NAMES})\s*\(`, "i");

/** Whether `formula` calls a function of the add-in (EAV_FORMULA): what Refresh and Save as Values act on. */
export const isEavFormula = (formula: string): boolean => EAV_FORMULA.test(formula);

CustomFunctions.associate("TR", TR);
CustomFunctions.associate("RHISTORY", RHISTORY);
CustomFunctions.associate("HISTORY", HISTORY);
CustomFunctions.associate("RSEARCH", RSEARCH);
CustomFunctions.associate("RTGET", RTGET);
CustomFunctions.associate("RTGETS", RTGETS);
CustomFunctions.associate("METADATA", METADATA);
CustomFunctions.associate("OVERVIEW", OVERVIEW);
CustomFunctions.associate("NEWS", NEWS);
CustomFunctions.associate("RTSTREAM", RTSTREAM);
CustomFunctions.associate("STORY", STORY);
CustomFunctions.associate("RDP.DATA", RDP_DATA);
CustomFunctions.associate("RDP.HISTORICALPRICING", RDP_HISTORICALPRICING);
CustomFunctions.associate("RDP.PRICE", RDP_PRICE);
CustomFunctions.associate("RDP.SEARCH", RDP_SEARCH);
CustomFunctions.associate("TRAGGREGATE", TRAGGREGATE);
CustomFunctions.associate("RDP.AGGREGATE", RDP_AGGREGATE);
CustomFunctions.associate("RDP.DATEISWD", RDP_DATEISWD);
CustomFunctions.associate("RDP.DATEADJTOWD", RDP_DATEADJTOWD);
CustomFunctions.associate("RDP.DATELASTWD", RDP_DATELASTWD);
CustomFunctions.associate("RDP.DATELISTHOLIDAYS", RDP_DATELISTHOLIDAYS);
CustomFunctions.associate("RDP.DATECALCPERIOD", RDP_DATECALCPERIOD);
CustomFunctions.associate("RDP.DATECOUNTPERIODS", RDP_DATECOUNTPERIODS);
CustomFunctions.associate("RDP.DATEFINDDATE", RDP_DATEFINDDATE);
CustomFunctions.associate("RDP.FXSPOT", RDP_FXSPOT);
CustomFunctions.associate("RDP.FXSWPTOSWP", RDP_FXSWPTOSWP);
CustomFunctions.associate("RDP.FXSWPTODEP", RDP_FXSWPTODEP);
CustomFunctions.associate("RDP.FXDEPTOSWP", RDP_FXDEPTOSWP);
CustomFunctions.associate("RDP.FXZCTOFRA", RDP_FXZCTOFRA);
CustomFunctions.associate("RDP.FXINFO", RDP_FXINFO);
CustomFunctions.associate("RDP.INTERP", RDP_INTERP);
CustomFunctions.associate("RDP.ANALYTICS", RDP_ANALYTICS);